import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { Landmark } from "lucide-react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import { useEffect, useState } from "react";

import { SettlementAccountsSelect } from "@/components/accounts-select";
import CurrencyFlag from "@/components/currency-flag";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { useCustomerAccount } from "@/providers";

import DepositDetailsInfo from "./index";

interface DepositDetailsDrawerProps {
  trigger: React.ReactNode;
  initialAccountId?: string;
  showAccountSelect?: boolean;
  className?: string;
}

export default function DepositDetailsDrawer({
  trigger,
  initialAccountId,
  showAccountSelect = true,
  className,
}: DepositDetailsDrawerProps) {
  const tDepositDetails = useTranslations("depositDetails");
  const isMobile = useIsMobile();
  const { productId } = useParams<{ productId?: string }>();
  const { customerAccountBaseCurrency } = useCustomerAccount();

  const [open, setOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useQueryState(
    "selectedAccount",
    {
      defaultValue: "",
    }
  );

  // Clear selectedAccount when drawer closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Wait for animation to complete before clearing
      setTimeout(() => {
        setSelectedAccount(null);
      }, 300);
    }
  };

  // Determine if we're on an account detail page
  const isAccountDetailPage = !!productId;
  const shouldShowAccountSelect = showAccountSelect && !isAccountDetailPage;

  // Set initial account
  useEffect(() => {
    if (open) {
      if (isAccountDetailPage && productId) {
        // On account detail page, use the current account
        setSelectedAccount(productId);
      } else if (!selectedAccount) {
        // Default to initialAccountId if provided, otherwise use base currency
        const defaultAccount = initialAccountId || customerAccountBaseCurrency;
        if (defaultAccount) {
          setSelectedAccount(defaultAccount);
        }
      }
    }
  }, [
    open,
    selectedAccount,
    isAccountDetailPage,
    productId,
    initialAccountId,
    customerAccountBaseCurrency,
    setSelectedAccount,
  ]);

  const title = (
    <div className="flex items-center gap-4 mx-1">
      {selectedAccount && !shouldShowAccountSelect ? (
        <>
          <CurrencyFlag
            size={isMobile ? "24px" : "28px"}
            currency={selectedAccount}
          />
          <div className={cn("font-medium text-base md:text-lg")}>
            {tDepositDetails("depositAccount", {
              product: selectedAccount,
            })}
          </div>
        </>
      ) : (
        <Landmark strokeWidth={2} className="size-5 text-green-600" />
      )}
      {(!selectedAccount || shouldShowAccountSelect) && (
        <span className={cn("font-medium text-base md:text-lg")}>
          {shouldShowAccountSelect
            ? tDepositDetails("selectDepositDetails")
            : tDepositDetails("depositFunds")}
        </span>
      )}
    </div>
  );

  const content = (
    <div
      className={cn(
        "flex flex-col flex-1",
        !isMobile && "p-0 max-h-[calc(90dvh-220px)]"
      )}
    >
      {shouldShowAccountSelect && (
        <div className="flex flex-1 lg:justify-center">
          <div
            className={cn(
              "flex flex-col gap-2 w-full items-center mb-4",
              // Desktop body is !p-0 — inset the select to match the scroll body.
              !isMobile && "px-4"
            )}
          >
            <label className={cn("font-semibold text-primary lg:text-center")}>
              {tDepositDetails("selectAccount")}
            </label>
            <SettlementAccountsSelect
              popoverAlign="center"
              currentAccountId={selectedAccount || undefined}
              className={cn(
                "text-base md:text-lg font-medium mt-1 rounded-full border border-brand-primary/30 bg-brand-primary/5 hover:bg-brand-primary/20 !py-1.5 !px-3 h-12",
                isMobile && "w-full flex-1"
              )}
              showFlag={true}
              onSelect={(accountId) => {
                setSelectedAccount(accountId);
              }}
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          // space-y-4 matches depositDetailsStyles.root so fiat/crypto
          // bodies sit on the same vertical rhythm under the currency select.
          // flex-col lets crypto network selection fill and vertically center.
          "space-y-4 overflow-y-auto flex-1 flex flex-col",
          // Desktop body is !p-0 — pad the scroll content so all product
          // types share the same horizontal inset. Mobile body already has p-4.
          !isMobile && "min-h-0 overflow-x-hidden px-4 pb-4",
          isMobile && "overflow-x-hidden max-h-[calc(100dvh-160px)]",
          isMobile && shouldShowAccountSelect && "max-h-[calc(100dvh-240px)]"
        )}
      >
        {selectedAccount && (
          <div className="flex flex-1 flex-col min-h-0">
            <DepositDetailsInfo accountId={selectedAccount} />
          </div>
        )}

        {!selectedAccount && !shouldShowAccountSelect && (
          <div className="text-center text-muted-foreground">
            {tDepositDetails("noAccountSelected")}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={handleOpenChange}
      trigger={trigger}
      title={title}
      className={className}
      contentClassName="!max-w-[800px] !overflow-x-hidden"
      headerClassName={isMobile ? "h-[64px]" : undefined}
      bodyClassName={cn(
        isMobile ? "p-4 space-y-4 flex-1" : "space-y-4",
        !isMobile && "!p-0"
      )}
      footerClassName={isMobile ? "!pt-0" : undefined}
    >
      {content}
    </ResponsiveDialog>
  );
}
